import { useEffect, useState, useRef } from "react";
import { Modal, Form, Button, Row, Col, Alert } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";

import {
  useListTechCommunitiesQuery,
  useTriggerTcParticipantRequestMutation,
} from "api/public/techCommunities/techCommunities.api";
import ReactSelect from "components/ReactSelect";

const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

export default function BecomeAUsTcForm({ show, setShowModal }) {
  const [tc, setTc] = useState({});
  const [error, setError] = useState(false);
  const [validated, setValidated] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);
  const [tcValid, setTcValid] = useState(true);
  const [errors, setErrors] = useState({});

  const recaptchaRef = useRef(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const { data: techCommunities = [] } = useListTechCommunitiesQuery({
    isInternational: false,
  });
  const [triggerTcParticipantRequest] =
    useTriggerTcParticipantRequestMutation();

  const communities = techCommunities.map((comm) => ({
    value: comm.community_id,
    label: comm.community_name,
  }));

  const handleChange = (e) => {
    if (e.target.name === "phone") {
      setErrors({
        ...errors,
        [e.target.name]:
          !/^[+]?([0-9][\s]?|[0-9]?)([(][0-9]{3}[)][\s]?|[0-9]{3}[-\s.]?)[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(
            e.target.value,
          ),
      });
    }
    setTc({ ...tc, [e.target.name]: e.target.value });
  };

  const testSelects = () => {
    if ("tc_communities" in tc) {
      return true;
    }
    if (!("tc_communities" in tc)) {
      setTcValid(false);
    }
    return false;
  };

  const handleSubmit = async (e) => {
    const form = e.currentTarget;
    e.preventDefault();
    testSelects();
    if (form.checkValidity() === false || testSelects() === false) {
      e.stopPropagation();
    } else if (!recaptchaToken) {
      setInvalidToken(true);
      e.stopPropagation();
    } else {
      try {
        await triggerTcParticipantRequest({
          name: tc?.name,
          affiliation: tc?.affiliation,
          email: tc?.email,
          phone: tc?.phone,
          statement: tc?.statement,
          community_ids: [tc?.tc_communities],
          recaptcha_token: recaptchaToken,
        }).unwrap();
        handleClose();
        setTcValid(true);
        recaptchaRef.current?.reset();
      } catch (submitError) {
        console.log(
          "Unable to submit become a technical community participant request: " +
            submitError,
        );
        setError(true);
      }
    }
    setValidated(true);
  };

  const handleClose = () => setShowModal(false);

  useEffect(() => {
    setTc({});
    setError(false);
    setValidated(false);
    setInvalidToken(false);
    setTcValid(true);
  }, [show]);

  return (
    <Modal size="lg" show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title className="text-primary">
          <h3>BECOME A TECHNICAL COMMUNITY PARTICIPANT</h3>
        </Modal.Title>
      </Modal.Header>
      <p className="mx-4 mt-3">
        Calls for participants for each Technical Community (TC) are sent to
        industry, government, end users, academic institutions, and labs as
        announcements posted on the NIAP website. All interested parties wanting
        to participate in any TC should provide the following information below:
      </p>
      <Form validated={validated} onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <Row>
            <Col sm={6}>
              <Form.Group className="mb-3" controlId="name">
                <Form.Label>Name: *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  onChange={handleChange}
                  placeholder="Name"
                  autoFocus
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="affiliation">
                <Form.Label>Affiliation: *</Form.Label>
                <Form.Control
                  type="text"
                  name="affiliation"
                  onChange={handleChange}
                  placeholder="Affiliation"
                  required
                />
                <Form.Text className="text-muted">
                  Vendor, CCTL, Academic Institution, Scheme, Other
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>E-mail Address: *</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  onChange={handleChange}
                  placeholder="Email"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="phone">
                <Form.Label>Telephone Number: *</Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  onChange={handleChange}
                  placeholder="+1 (111)111-1111"
                  isInvalid={errors.phone}
                  required
                />
                <Form.Text className="text-muted">
                  Specify country code, DSN, comm., etc., as applicable
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3" controlId="token">
                {show && (
                  <ReCAPTCHA
                    id="form-recaptcha"
                    sitekey={siteKey}
                    ref={recaptchaRef}
                    onChange={(token) => {
                      setRecaptchaToken(token);
                    }}
                    onExpired={() => setRecaptchaToken(null)}
                  />
                )}
                {invalidToken ? (
                  <p className="text-danger">Please Confirm.</p>
                ) : (
                  <Form.Text className="text-muted">
                    Required to submit any information via this form.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className="mb-3" controlId="tc_communities">
                <Form.Label>Select TC Communities: *</Form.Label>
                <ReactSelect
                  id={"tc_communities"}
                  options={communities}
                  newObj={tc}
                  handleChange={handleChange}
                  name={"tc_communities"}
                  isValid={tcValid}
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="statement">
                <Form.Label>
                  A brief statement of the qualifications for participation in
                  the TC: *
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  name="statement"
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="warning" type="submit">
            Submit
          </Button>
        </Modal.Footer>
      </Form>
      {error && (
        <Alert variant="danger" className="mt-3">
          Failed to send Message
        </Alert>
      )}
    </Modal>
  );
}
